// Owner Logins: each owner's sign-ins for NaTIS, PayCity and the like, kept
// where the whole team can reach them. db/migrations/8004.
//
// The password is never in the list. It lives in Supabase Vault and comes back
// only when someone presses Show, one login at a time, and that press is
// written down with who and when; the last one is shown beside each login.
// The read-only demo login can see which portals are on file but never a
// password, refused twice: by Postgres, and by the Worker refusing it every
// route that is not get*.
//
// A shown password hides itself again after thirty seconds, so a screen left
// open does not keep it on display.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Copy, Eye, EyeOff, KeyRound, Pencil, Plus, Trash2, X } from 'lucide-react'
import { useArchiveOwnerLogin, useGetOwnerLogins, useRevealOwnerLogin, useSaveOwnerLogin } from '../hooks/backend/ownerLogins'
import { Button } from '../lib/shadcn/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../lib/shadcn/dialog'
import { Input } from '../lib/shadcn/input'
import { NativeSelect } from '../lib/shadcn/native-select'
import { Spinner } from '../lib/shadcn/spinner'
import { ErrorNote, PageGate } from '../components/payments/requestParts'
import { formatWhen } from '../utils/loans'

type OwnerLogin = {
  id: string; owner_id: string; owner_name: string; vehicle_id: string | null; vehicle: string | null
  portal: string; login_url: string | null; username: string; notes: string | null
  has_password: boolean; password_changed_at: string | null
  created_by_name: string | null; created_at: string; updated_by_name: string | null; updated_at: string
  last_revealed_at: string | null; last_revealed_by_name: string | null; reveals: number
}

type OwnerLoginList = {
  can_view: boolean; can_edit: boolean; can_reveal: boolean
  portals: string[]
  owners: { id: string; name: string; status: string; plates: string | null }[] | null
  vehicles: { id: string; owner_id: string | null; label: string }[] | null
  rows: OwnerLogin[]
}

const HIDE_AFTER_MS = 30_000

export default function OwnerLogins() {
  const list = useGetOwnerLogins<OwnerLoginList | null>()
  const [owner, setOwner] = useState('')
  const [search, setSearch] = useState('')
  const [editing, setEditing] = useState<OwnerLogin | 'new' | null>(null)
  const [settled, setSettled] = useState(false)

  const load = list.trigger
  const refresh = useCallback(() => {
    void load({ owner_id: owner || null }, { skipCache: true }).catch(() => undefined).finally(() => setSettled(true))
  }, [load, owner])
  useEffect(() => { refresh() }, [refresh])

  const data = list.data ?? null
  const rows = useMemo(() => {
    const q = search.trim().toLowerCase()
    const all = data?.rows ?? []
    if (!q) return all
    return all.filter((r) => [r.owner_name, r.portal, r.username, r.vehicle ?? '', r.notes ?? ''].some((x) => x.toLowerCase().includes(q)))
  }, [data, search])

  return (
    <PageGate
      loading={!settled}
      error={data === null ? list.error : null}
      denied={settled && data === null && !list.error && !list.loading}
      onRetry={refresh}
      deniedTitle="Owner logins are open to Fleetly staff."
      deniedHint="If you should see them, ask an admin to link your login in cockpit access."
    >
      <div className="flex h-full min-h-0 flex-col bg-card">
        <div className="flex flex-wrap items-center gap-3 border-b border-border px-4 py-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]">
            <KeyRound className="h-5 w-5" />
          </div>
          <div className="min-w-[12rem] flex-1">
            <h1 className="text-lg font-semibold text-foreground">Owner Logins</h1>
            <p className="text-xs text-muted-foreground">
              Each owner's sign-ins for NaTIS, PayCity and other sites. Passwords are stored encrypted, and every time one is shown it is recorded with your name.
            </p>
          </div>
          {data?.can_edit && (
            <Button variant="brand" size="sm" onClick={() => setEditing('new')}>
              <Plus className="mr-1 h-4 w-4" /> Add a login
            </Button>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-2">
          <Input className="h-8 w-56 text-xs" placeholder="Search owner, site, username, car" value={search} onChange={(e) => setSearch(e.target.value)} />
          <NativeSelect className="h-8 w-56 text-xs" value={owner} onChange={(e) => setOwner(e.target.value)} aria-label="Owner">
            <option value="">Every owner</option>
            {(data?.owners ?? []).map((o) => <option key={o.id} value={o.id}>{o.name}{o.plates ? ` (${o.plates})` : ''}</option>)}
          </NativeSelect>
          <span className="ml-auto text-xs text-muted-foreground">{rows.length} login{rows.length === 1 ? '' : 's'}</span>
        </div>

        {list.error && data !== null && <div className="border-b border-border px-4 py-2"><ErrorNote>{list.error}</ErrorNote></div>}

        <div className="min-h-0 flex-1 overflow-y-auto">
          {rows.length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-muted-foreground">
              {(data?.rows ?? []).length === 0 ? 'No owner logins are stored yet. Add the first with the button above.' : 'No login matches this search.'}
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {rows.map((r) => (
                <LoginItem key={r.id} login={r} canEdit={data?.can_edit === true} canReveal={data?.can_reveal === true}
                  onEdit={() => setEditing(r)} onChanged={refresh} />
              ))}
            </ul>
          )}
        </div>

        <LoginDialog editing={editing} onClose={() => setEditing(null)} data={data} onSaved={refresh} />
      </div>
    </PageGate>
  )
}

function LoginItem({ login: r, canEdit, canReveal, onEdit, onChanged }: {
  login: OwnerLogin; canEdit: boolean; canReveal: boolean; onEdit: () => void; onChanged: () => void
}) {
  const reveal = useRevealOwnerLogin<{ password: string }>()
  const archive = useArchiveOwnerLogin()
  const [shown, setShown] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const [removing, setRemoving] = useState(false)
  const [why, setWhy] = useState('')
  const [error, setError] = useState<string | null>(null)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => () => { if (timer.current) clearTimeout(timer.current) }, [])

  async function show() {
    setError(null)
    try {
      const res = await reveal.trigger({ id: r.id })
      setShown(res?.password ?? '')
      if (timer.current) clearTimeout(timer.current)
      timer.current = setTimeout(() => setShown(null), HIDE_AFTER_MS)
      // The reveal is now on record; refresh so "last shown" says so.
      onChanged()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }

  async function copy() {
    if (shown === null) return
    try {
      await navigator.clipboard.writeText(shown)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      setError('The browser would not copy it. Select the password and copy it by hand.')
    }
  }

  async function remove() {
    setError(null)
    try {
      await archive.trigger({ id: r.id, reason: why })
      setRemoving(false)
      onChanged()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }

  return (
    <li className="space-y-2 px-4 py-3">
      <div className="flex flex-wrap items-start gap-x-4 gap-y-2">
        <div className="min-w-[12rem] flex-1">
          <p className="text-sm font-medium text-foreground">{r.owner_name} <span className="font-normal text-muted-foreground">· {r.portal}{r.vehicle ? ` · ${r.vehicle}` : ''}</span></p>
          {r.login_url && (
            <a href={r.login_url} target="_blank" rel="noreferrer noopener" className="text-xs text-[hsl(var(--brand))] underline-offset-2 hover:underline">{r.login_url}</a>
          )}
          {r.notes && <p className="text-xs text-muted-foreground">{r.notes}</p>}
        </div>
        <div className="min-w-[14rem] space-y-1 text-xs">
          <p><span className="text-muted-foreground">Username </span><span className="font-mono text-foreground">{r.username}</span></p>
          <div className="flex flex-wrap items-center gap-1">
            <span className="text-muted-foreground">Password </span>
            {!r.has_password ? (
              <span className="text-muted-foreground">none stored</span>
            ) : shown !== null ? (
              <>
                <span className="select-all rounded bg-muted px-1.5 py-0.5 font-mono text-foreground">{shown}</span>
                <Button size="sm" variant="ghost" className="h-6 px-1.5" onClick={() => void copy()} aria-label="Copy password">
                  <Copy className="h-3.5 w-3.5" />{copied && <span className="ml-1">Copied</span>}
                </Button>
                <Button size="sm" variant="ghost" className="h-6 px-1.5" onClick={() => setShown(null)} aria-label="Hide password"><EyeOff className="h-3.5 w-3.5" /></Button>
              </>
            ) : (
              <>
                <span className="font-mono text-muted-foreground">••••••••</span>
                {canReveal && (
                  <Button size="sm" variant="outline" className="h-6 px-2" disabled={reveal.loading} onClick={() => void show()}>
                    {reveal.loading ? <Spinner className="mr-1 h-3 w-3" /> : <Eye className="mr-1 h-3 w-3" />} Show
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
        {canEdit && (
          <div className="flex gap-1">
            <Button size="sm" variant="ghost" className="h-7 px-2" onClick={onEdit} aria-label="Edit login"><Pencil className="h-3.5 w-3.5" /></Button>
            <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => setRemoving(true)} aria-label="Remove login"><Trash2 className="h-3.5 w-3.5" /></Button>
          </div>
        )}
      </div>
      <p className="text-[11px] text-muted-foreground">
        Added by {r.created_by_name ?? '—'} {formatWhen(r.created_at)}
        {r.password_changed_at && r.password_changed_at !== r.created_at ? `, password changed ${formatWhen(r.password_changed_at)}` : ''}
        {r.last_revealed_at ? `. Last shown to ${r.last_revealed_by_name ?? '—'} ${formatWhen(r.last_revealed_at)}, ${r.reveals} time${r.reveals === 1 ? '' : 's'} in all.` : '. Never shown.'}
      </p>
      {removing && (
        <span className="flex flex-wrap items-center gap-1">
          <Input className="h-8 w-64 text-xs" placeholder="Why it is being removed" value={why} onChange={(e) => setWhy(e.target.value)} />
          <Button size="sm" variant="destructive" disabled={archive.loading} onClick={() => void remove()}>Remove it</Button>
          <Button size="sm" variant="ghost" onClick={() => setRemoving(false)}><X className="h-3.5 w-3.5" /></Button>
        </span>
      )}
      {error && <ErrorNote>{error}</ErrorNote>}
    </li>
  )
}

function LoginDialog({ editing, onClose, data, onSaved }: {
  editing: OwnerLogin | 'new' | null; onClose: () => void; data: OwnerLoginList | null; onSaved: () => void
}) {
  const save = useSaveOwnerLogin<{ ok: boolean; id: string }>()
  const [ownerId, setOwnerId] = useState('')
  const [vehicleId, setVehicleId] = useState('')
  // The site is chosen from the list Postgres sends (NaTIS, PayCity, ...), with
  // Other opening a box. It was a text box with suggestions, and opening on
  // "NaTIS" filtered the suggestions down to NaTIS, so PayCity never showed.
  const [site, setSite] = useState('')
  const [otherSite, setOtherSite] = useState('')
  const [url, setUrl] = useState('')
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [notes, setNotes] = useState('')
  const [showPw, setShowPw] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const isNew = editing === 'new'

  useEffect(() => {
    if (!editing) return
    const e = editing === 'new' ? null : editing
    const listed = (data?.portals ?? []).filter((p) => p !== 'Other')
    const known = e ? listed.find((p) => p.toLowerCase() === e.portal.toLowerCase()) : undefined
    setOwnerId(e?.owner_id ?? ''); setVehicleId(e?.vehicle_id ?? '')
    setSite(!e ? '' : known ?? 'Other'); setOtherSite(e && !known ? e.portal : '')
    setUrl(e?.login_url ?? ''); setUsername(e?.username ?? ''); setPassword(''); setNotes(e?.notes ?? '')
    setShowPw(false); setError(null)
    // Reset only when the dialog opens: a background refresh of the list must
    // not wipe what someone is typing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing])

  const cars = (data?.vehicles ?? []).filter((v) => !ownerId || v.owner_id === ownerId)

  async function submit() {
    setError(null)
    try {
      await save.trigger({
        id: isNew || !editing ? null : editing.id, owner_id: ownerId || null, vehicle_id: vehicleId || null,
        portal: site === 'Other' ? otherSite : site, login_url: url, username, password, notes,
      })
      onSaved()
      onClose()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }

  return (
    <Dialog open={editing !== null} onOpenChange={(o) => { if (!o && !save.loading) onClose() }}>
      <DialogContent className="max-h-[92vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isNew ? 'Add an owner login' : 'Change this login'}</DialogTitle>
          <DialogDescription>
            The password is stored encrypted and is shown only when someone presses Show, with their name recorded.
            {!isNew && ' Leave the password empty to keep the one stored.'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="space-y-1">
            <label htmlFor="ol-owner" className="text-xs font-medium text-foreground">Owner</label>
            <NativeSelect id="ol-owner" value={ownerId} onChange={(e) => { setOwnerId(e.target.value); setVehicleId('') }}>
              <option value="">Choose the owner</option>
              {(data?.owners ?? []).map((o) => <option key={o.id} value={o.id}>{o.name}{o.plates ? ` (${o.plates})` : ''}{o.status !== 'active' ? `, ${o.status}` : ''}</option>)}
            </NativeSelect>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <label htmlFor="ol-portal" className="text-xs font-medium text-foreground">Site</label>
              <NativeSelect id="ol-portal" value={site} onChange={(e) => setSite(e.target.value)}>
                <option value="">Choose the site</option>
                {(data?.portals ?? []).map((p) => <option key={p} value={p}>{p}</option>)}
              </NativeSelect>
              {site === 'Other' && (
                <Input aria-label="Which site" className="mt-1" placeholder="Which site, e.g. Santam" value={otherSite} onChange={(e) => setOtherSite(e.target.value)} />
              )}
            </div>
            <div className="space-y-1">
              <label htmlFor="ol-car" className="text-xs font-medium text-foreground">Car (optional)</label>
              <NativeSelect id="ol-car" value={vehicleId} onChange={(e) => setVehicleId(e.target.value)}>
                <option value="">Every car of this owner</option>
                {cars.map((v) => <option key={v.id} value={v.id}>{v.label}</option>)}
              </NativeSelect>
            </div>
          </div>
          <div className="space-y-1">
            <label htmlFor="ol-url" className="text-xs font-medium text-foreground">Web address (optional)</label>
            <Input id="ol-url" placeholder="e.g. online.natis.gov.za" value={url} onChange={(e) => setUrl(e.target.value)} />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <label htmlFor="ol-user" className="text-xs font-medium text-foreground">Username, email or ID number</label>
              <Input id="ol-user" autoComplete="off" value={username} onChange={(e) => setUsername(e.target.value)} />
            </div>
            <div className="space-y-1">
              <label htmlFor="ol-pw" className="text-xs font-medium text-foreground">{isNew ? 'Password' : 'New password (optional)'}</label>
              <div className="flex gap-1">
                <Input id="ol-pw" type={showPw ? 'text' : 'password'} autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} />
                <Button type="button" size="sm" variant="ghost" className="h-9 px-2" onClick={() => setShowPw((v) => !v)} aria-label={showPw ? 'Hide password' : 'Show password'}>
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
            </div>
          </div>
          <div className="space-y-1">
            <label htmlFor="ol-notes" className="text-xs font-medium text-foreground">Notes (optional)</label>
            <Input id="ol-notes" placeholder="e.g. OTP goes to the owner's phone" value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
          {error && <ErrorNote>{error}</ErrorNote>}
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={onClose} disabled={save.loading}>Cancel</Button>
            <Button variant="brand" onClick={() => void submit()} disabled={save.loading}>
              {save.loading ? <Spinner className="mr-1 h-4 w-4" /> : <KeyRound className="mr-1 h-4 w-4" />} Save
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
